/**
 * Execuções da integração com o Bioslab — uma linha por EXECUÇÃO.
 *
 * Não uma por guia. A consulta vai ao Bioslab em lotes (`IN` com N guias por
 * vez), então um arquivo de 899 guias são cerca de 5 consultas e **um** evento
 * de auditoria. É esse o grão que interessa a quem precisa responder o que
 * rodou contra a produção do cliente, quando, por quem e com que resultado.
 *
 * O detalhe mostra a consulta aplicada com os placeholders no lugar dos
 * valores: dá para auditar exatamente o que rodou, sem os números das guias
 * ficarem gravados na trilha.
 */
import { useCallback, useEffect, useState } from 'react';
import { mensagemErro } from '../api/axios';
import { detalheIntegracao, listarIntegracoes } from '../api/fluxfin';
import {
  Alerta,
  Badge,
  Botao,
  LoadingSpinner,
  Painel,
  Tabela,
  Th,
  Vazio,
} from './common';
import { formatDateTime, numero } from '../utils/formato';

function duracao(ms) {
  if (!ms) return '—';
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}

/* ── Detalhe de uma execução ────────────────────────────────────────────── */
function ModalDetalhe({ id, aoFechar }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');

  useEffect(() => {
    detalheIntegracao(id)
      .then((r) => setDados(r.data))
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível abrir a execução.')));
  }, [id]);

  return (
    <>
      <div
        className="modal-backdrop fade show"
        onClick={aoFechar}
        style={{ background: 'rgba(16,24,40,.5)' }}
      />
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-xl modal-dialog-scrollable modal-dialog-centered">
          <div
            className="modal-content"
            style={{ borderRadius: 16, border: '1px solid var(--ff-border)' }}
          >
            <div className="modal-header" style={{ borderColor: 'var(--ff-border-soft)' }}>
              <div>
                <h2 className="ff-painel__titulo">Execução da integração</h2>
                <p className="ff-painel__sub">
                  {dados
                    ? `${dados.arquivo || 'sem arquivo'} · ${formatDateTime(dados.created_at)}`
                    : 'carregando…'}
                </p>
              </div>
              <button className="btn-close" onClick={aoFechar} aria-label="Fechar" />
            </div>

            <div className="modal-body">
              <Alerta tipo="erro">{erro}</Alerta>
              {!dados ? (
                <LoadingSpinner />
              ) : (
                <>
                  <Alerta tipo={dados.sucesso ? 'ok' : 'erro'}>
                    {dados.sucesso ? (
                      <>
                        Consulta concluída em <strong>{duracao(dados.execucao.duracao_ms)}</strong>.
                      </>
                    ) : (
                      <>
                        <strong>A consulta falhou.</strong> {dados.erro}
                      </>
                    )}
                  </Alerta>

                  <h3 className="mt-4 mb-2" style={{ fontSize: 14, fontWeight: 600 }}>
                    Execução
                  </h3>
                  <div className="ff-indicadores">
                    <div>
                      <div className="ff-indicador__rotulo">Guias consultadas</div>
                      <div className="ff-indicador__valor">
                        {numero(dados.execucao.guias_consultadas)}
                      </div>
                    </div>
                    <div>
                      <div className="ff-indicador__rotulo">Consultas ao banco</div>
                      <div className="ff-indicador__valor">{numero(dados.execucao.lotes)}</div>
                      <div className="ff-indicador__nota">
                        lotes de {numero(dados.execucao.tamanho_lote)} guias
                      </div>
                    </div>
                    <div>
                      <div className="ff-indicador__rotulo">Linhas retornadas</div>
                      <div className="ff-indicador__valor">
                        {numero(dados.execucao.linhas_retornadas)}
                      </div>
                      <div className="ff-indicador__nota">uma por exame</div>
                    </div>
                    <div>
                      <div className="ff-indicador__rotulo">Duração</div>
                      <div className="ff-indicador__valor">
                        {duracao(dados.execucao.duracao_ms)}
                      </div>
                    </div>
                  </div>

                  <h3 className="mt-4 mb-2" style={{ fontSize: 14, fontWeight: 600 }}>
                    Resultado
                  </h3>
                  <div className="ff-indicadores">
                    <div>
                      <div className="ff-indicador__rotulo">Resolvidas</div>
                      <div className="ff-indicador__valor">
                        {numero(dados.resultado.resolvidas)}
                      </div>
                    </div>
                    <div>
                      <div className="ff-indicador__rotulo">Pendentes</div>
                      <div className="ff-indicador__valor">
                        {numero(dados.resultado.pendentes)}
                      </div>
                    </div>
                    <div>
                      <div className="ff-indicador__rotulo">Já conhecidas</div>
                      <div className="ff-indicador__valor">
                        {numero(dados.resultado.ja_conhecidas)}
                      </div>
                      <div className="ff-indicador__nota">não foram consultadas</div>
                    </div>
                  </div>

                  {dados.guias_nao_resolvidas?.length > 0 && (
                    <>
                      <h3 className="mt-4 mb-2" style={{ fontSize: 14, fontWeight: 600 }}>
                        Guias que não resolveram
                      </h3>
                      <p style={{ fontSize: 13, color: 'var(--ff-muted)' }}>
                        {dados.guias_nao_resolvidas_truncada
                          ? 'Lista truncada nas 200 primeiras — as contagens acima dão o total.'
                          : 'Estas guias estão na tela de Pendências.'}
                      </p>
                      <div
                        className="ff-mono"
                        style={{
                          fontSize: 12,
                          maxHeight: 160,
                          overflow: 'auto',
                          background: 'var(--ff-surface-2, #f6f8fa)',
                          borderRadius: 8,
                          padding: '10px 12px',
                        }}
                      >
                        {dados.guias_nao_resolvidas.join(' · ')}
                      </div>
                    </>
                  )}

                  <details className="mt-4">
                    <summary style={{ cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                      Consulta aplicada no Bioslab
                    </summary>
                    <p className="mt-2" style={{ fontSize: 13, color: 'var(--ff-muted)' }}>
                      Os números das guias entram como parâmetros, no lugar dos
                      marcadores — não são gravados na trilha. A consulta é{' '}
                      <strong>somente leitura</strong>.
                    </p>
                    <pre
                      className="ff-mono"
                      style={{ fontSize: 12, maxHeight: 320, overflow: 'auto' }}
                    >
                      {dados.query_modelo || '(não registrada)'}
                    </pre>
                  </details>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function IntegracoesBioslab() {
  const [dados, setDados] = useState({ itens: [], total: 0 });
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [pagina, setPagina] = useState(1);
  const [aberta, setAberta] = useState(null);

  const carregar = useCallback(() => {
    setCarregando(true);
    listarIntegracoes({ pagina, tamanho: 25 })
      .then((r) => setDados(r.data))
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível carregar as execuções.')))
      .finally(() => setCarregando(false));
  }, [pagina]);

  useEffect(carregar, [carregar]);

  const paginas = Math.max(1, Math.ceil(dados.total / 25));

  return (
    <>
      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>

      <Painel
        titulo="Execuções da integração"
        subtitulo="Uma linha por execução, não por guia — a consulta vai ao Bioslab em lotes."
      >
        {carregando ? (
          <LoadingSpinner />
        ) : dados.itens.length === 0 ? (
          <Vazio
            icone="bi-database"
            titulo="Nenhuma execução registrada"
            descricao="Cada vez que o sistema consulta o Bioslab, o evento aparece aqui."
          />
        ) : (
          <Tabela>
            <thead>
              <tr>
                <Th>Quando</Th>
                <Th>Arquivo</Th>
                <Th>Origem</Th>
                <Th>Situação</Th>
                <Th alinhar="right">Guias</Th>
                <Th alinhar="right">Consultas</Th>
                <Th alinhar="right">Resolvidas</Th>
                <Th alinhar="right">Pendentes</Th>
                <Th alinhar="right">Duração</Th>
                <Th alinhar="right">Detalhe</Th>
              </tr>
            </thead>
            <tbody>
              {dados.itens.map((e) => (
                <tr key={e.id_consulta}>
                  <td className="col-data">{formatDateTime(e.created_at)}</td>
                  <td className="col-meta">{e.arquivo || '—'}</td>
                  <td className="col-meta">
                    {e.origem === 'pendencias' ? 'Pendências' : 'Arquivo'}
                  </td>
                  <td>
                    <Badge tom={e.sucesso ? 'confirmado' : 'reprovado'}>
                      {e.sucesso ? 'Concluída' : 'Falhou'}
                    </Badge>
                  </td>
                  <td className="col-qtd">{numero(e.qtd_guias_consultadas)}</td>
                  <td className="col-qtd">{numero(e.qtd_lotes)}</td>
                  <td className="col-qtd">{numero(e.qtd_resolvidas)}</td>
                  <td className="col-qtd">{numero(e.qtd_pendentes)}</td>
                  <td className="col-qtd">{duracao(e.duracao_ms)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Botao
                      variante="secundario"
                      pequeno
                      onClick={() => setAberta(e.id_consulta)}
                    >
                      Abrir
                    </Botao>
                  </td>
                </tr>
              ))}
            </tbody>
          </Tabela>
        )}

        {paginas > 1 && (
          <div className="d-flex justify-content-between align-items-center p-3">
            <span style={{ fontSize: 13, color: 'var(--ff-muted)' }}>
              Página {pagina} de {paginas} · {numero(dados.total)} execuções
            </span>
            <div className="d-flex gap-2">
              <Botao
                variante="secundario"
                pequeno
                onClick={() => setPagina((p) => Math.max(1, p - 1))}
                disabled={pagina <= 1}
              >
                Anterior
              </Botao>
              <Botao
                variante="secundario"
                pequeno
                onClick={() => setPagina((p) => Math.min(paginas, p + 1))}
                disabled={pagina >= paginas}
              >
                Próxima
              </Botao>
            </div>
          </div>
        )}
      </Painel>

      {aberta && <ModalDetalhe id={aberta} aoFechar={() => setAberta(null)} />}
    </>
  );
}
